import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { StylesProvider } from '@mui/styles';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import { Font } from '@react-pdf/renderer';
import FlagProvider from '@unleash/proxy-client-react';

import './assets/styles/index.scss';
import { theme, unleashConfig } from './configs';
import { RootNavigator } from './navigators';
import reportWebVitals from './reportWebVitals';
import { persistedStore } from './store';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string);
const fonts = [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;900&display=swap' }];

const MontserratMedium = require('./assets/fonts/Montserrat/Montserrat-Medium.ttf'); // eslint-disable-line
const MontserratSemiBold = require('./assets/fonts/Montserrat/Montserrat-SemiBold.ttf'); // eslint-disable-line
const MontserratMediumItalic = require('./assets/fonts/Montserrat/Montserrat-MediumItalic.ttf'); // eslint-disable-line
const MontserratBold = require('./assets/fonts/Montserrat/Montserrat-Bold.ttf'); // eslint-disable-line

Font.register({ family: 'Montserrat-Medium', src: MontserratMedium });
Font.register({ family: 'Montserrat-SemiBold', src: MontserratSemiBold });
Font.register({ family: 'Montserrat-MediumItalic', src: MontserratMediumItalic });
Font.register({ family: 'Montserrat-Bold', src: MontserratBold });

// enable sentry in production
const hosts = { prod: 'app.cleancode.us', sandbox: 'sandbox-app.cleancode.us' };

if (Object.values(hosts).includes(window.location.host)) {
  const environment = window.location.host.includes('sandbox') ? 'sandbox' : 'prod';

  console.log('Sentry Init: ', environment);

  Sentry.init({
    dsn: 'https://0b162a2c677a4fa6b1cf068accdf9673@o1077177.ingest.sentry.io/6086458',
    environment,
    debug: environment === 'sandbox',
  });
}

ReactDOM.render(
  <ThemeProvider theme={theme}>
    <StylesProvider injectFirst>
      <Provider store={persistedStore.store}>
        <PersistGate loading={null} persistor={persistedStore.persistor}>
          <FlagProvider config={unleashConfig}>
            <BrowserRouter>
              <Elements stripe={stripePromise} options={{ fonts }}>
                <CssBaseline />

                <RootNavigator />
              </Elements>
            </BrowserRouter>
          </FlagProvider>
        </PersistGate>
      </Provider>
    </StylesProvider>
  </ThemeProvider>,
  document.getElementById('root'),
);

reportWebVitals();
